import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { abrirFicheiroDaResposta, descarregarBlob } from '../utils/abrirFicheiro';

/* ============================================================
   RMM — RECURSOS DE MONITORIZAÇÃO E MEDIÇÃO
   ISO 9001 — 7.1.5 · P_IM_01 · IT.IM.01 (verificação interna)

   Os RMM são permanentes (código fixo, um trabalhador). O que é anual
   são as 12 verificações mensais — por isso o seletor de ano filtra a
   grelha de verificações, não a lista de instrumentos.

   O inventário que conta é o das fichas de Equipamentos com categoria RMM
   (é lá que entram os carregamentos do terreno e de onde sai a etiqueta QR).
   Cada RMM daqui aponta a uma dessas fichas e vai lá buscar a identificação;
   o que é do Sistema Integrado — código, estado e verificações — fica aqui.
   ============================================================ */

const MESES_CURTO = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// Nome do PDF de uma verificação: RMM_2026-001.pdf (o nº do relatório traz
// barras, que não podem ir num nome de ficheiro).
const nomeRelatorio = (v) => `RMM_${String(v.relatorio_num || v.id).replace(/^RMM\//, '').replace(/\//g, '-')}.pdf`;

const ESTADOS = {
    ativo: { label: 'Ativo', bg: '#dcfce7', fg: '#166534' },
    fora_servico: { label: 'Fora de serviço', bg: '#fee2e2', fg: '#991b1b' },
    substituido: { label: 'Substituído', bg: '#f1f5f9', fg: '#64748b' },
};

/* ---------- Criar / editar RMM ---------- */
function RmmModal({ rmm, tipos, trabalhadores, equipamentos, onClose, onSaved }) {
    const novo = !rmm?.id;
    const [form, setForm] = useState({
        codigo: rmm?.codigo || '',
        tipo: rmm?.tipo || 'fita_metrica',
        designacao: rmm?.designacao || '',
        marca: rmm?.marca || '',
        modelo: rmm?.modelo || '',
        n_serie: rmm?.n_serie || '',
        trabalhador_id: rmm?.trabalhador_id || '',
        data_aquisicao: rmm?.data_aquisicao ? rmm.data_aquisicao.substring(0, 10) : '',
        estado: rmm?.estado || 'ativo',
        observacoes: rmm?.observacoes || '',
        equipamento_id: rmm?.equipamento_id || '',
    });
    const [err, setErr] = useState('');
    const [guardando, setGuardando] = useState(false);

    // Num RMM novo, sugerir o próximo código livre do tipo escolhido.
    useEffect(() => {
        if (!novo) return;
        let cancelado = false;
        api.get('/sistema-integrado/rmm/proximo-codigo', { params: { tipo: form.tipo } })
            .then(r => { if (!cancelado) setForm(f => ({ ...f, codigo: r.data.codigo })); })
            .catch(() => { });
        return () => { cancelado = true; };
    }, [form.tipo, novo]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // Ficha de equipamento ligada: é dela que vem a identificação (aqui só se lê).
    // Escolhem-se as fichas da categoria RMM ainda livres, mais a que já está ligada.
    const ficha = equipamentos.find(e => String(e.id) === String(form.equipamento_id)) || null;
    const fichasDisponiveis = equipamentos.filter(e => !e.rmm_id || e.rmm_id === rmm?.id);
    const val = (campoFicha, campoForm) => (ficha ? (campoFicha || '—') : form[campoForm]);

    const guardar = async () => {
        if (!form.codigo.trim()) { setErr('O código é obrigatório.'); return; }
        setGuardando(true);
        setErr('');
        try {
            const payload = {
                ...form,
                trabalhador_id: form.trabalhador_id || null,
                equipamento_id: form.equipamento_id || null,
            };
            if (novo) await api.post('/sistema-integrado/rmm', payload);
            else await api.put(`/sistema-integrado/rmm/${rmm.id}`, payload);
            onSaved();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 16px' }}>{novo ? 'Novo RMM' : `Editar ${rmm.codigo}`}</h3>

                <div style={{ marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Ficha do equipamento</label>
                    <select value={form.equipamento_id} onChange={e => set('equipamento_id', e.target.value)} style={{ ...inp, width: '100%' }}>
                        <option value="">— sem ficha de equipamento —</option>
                        {fichasDisponiveis.map(e => (
                            <option key={e.id} value={e.id}>
                                {e.nome}{e.n_serie ? ` · nº ${e.n_serie}` : ''}
                            </option>
                        ))}
                    </select>
                    <p style={{ margin: '4px 0 0', fontSize: '0.74rem', color: ficha ? '#0369a1' : '#b45309' }}>
                        {ficha
                            ? 'A identificação e a pessoa afeta vêm desta ficha — editam-se em Equipamentos & Ferramentas.'
                            : 'Sem ficha ligada este RMM não consta do inventário de Equipamentos. Ligue-o à ficha correspondente (categoria RMM).'}
                    </p>
                </div>

                <div data-liquid-grid="true" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Tipo</label>
                        <select value={form.tipo} onChange={e => set('tipo', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {tipos.map(t => <option key={t.valor} value={t.valor}>{t.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Código (permanente)</label>
                        <input value={form.codigo} onChange={e => set('codigo', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Designação</label>
                        {ficha
                            ? <div style={lido}>{val(ficha.nome, 'designacao')}</div>
                            : <input value={form.designacao} onChange={e => set('designacao', e.target.value)} style={{ ...inp, width: '100%' }} />}
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Afeto ao trabalhador</label>
                        {ficha
                            ? <div style={lido}>{ficha.trabalhador_nome || '— sem afetação —'}</div>
                            : (
                                <select value={form.trabalhador_id} onChange={e => set('trabalhador_id', e.target.value)} style={{ ...inp, width: '100%' }}>
                                    <option value="">— sem afetação —</option>
                                    {trabalhadores.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                                </select>
                            )}
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Marca</label>
                        {ficha
                            ? <div style={lido}>{val(ficha.marca, 'marca')}</div>
                            : <input value={form.marca} onChange={e => set('marca', e.target.value)} style={{ ...inp, width: '100%' }} />}
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Modelo</label>
                        {ficha
                            ? <div style={lido}>{val(ficha.modelo, 'modelo')}</div>
                            : <input value={form.modelo} onChange={e => set('modelo', e.target.value)} style={{ ...inp, width: '100%' }} />}
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Nº de série</label>
                        {ficha
                            ? <div style={lido}>{val(ficha.n_serie, 'n_serie')}</div>
                            : <input value={form.n_serie} onChange={e => set('n_serie', e.target.value)} style={{ ...inp, width: '100%' }} />}
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Data de aquisição</label>
                        <input type="date" value={form.data_aquisicao} onChange={e => set('data_aquisicao', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Estado</label>
                        <select value={form.estado} onChange={e => set('estado', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                </div>

                <div style={{ marginTop: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Observações</label>
                    <textarea value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
                        rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                {err && <div style={{ color: '#e53e3e', marginTop: 10, fontSize: '0.85rem' }}>{err}</div>}

                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={guardando} style={btnPri}>
                        {guardando ? 'A guardar…' : 'Guardar'}
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Verificação mensal ---------- */
function VerificacaoModal({ rmm, ano, mes, verificacao, itens, trabalhadores, onClose, onSaved }) {
    const [checklist, setChecklist] = useState(() => {
        if (verificacao?.checklist && Object.keys(verificacao.checklist).length) return verificacao.checklist;
        // Por omissão tudo Conforme — o TAM corrige o que não estiver.
        return itens.filter(i => !i.grupo).reduce((acc, i) => {
            acc[`item${i.id}`] = { status: 'C', obs: '' };
            return acc;
        }, {});
    });
    const [data, setData] = useState(
        verificacao?.data_verificacao
            ? verificacao.data_verificacao.substring(0, 10)
            : new Date(ano, mes - 1, Math.min(new Date().getDate(), 28)).toISOString().substring(0, 10)
    );
    const [verificadoPor, setVerificadoPor] = useState(verificacao?.verificado_por || rmm.trabalhador_id || '');
    const [observacoes, setObservacoes] = useState(verificacao?.observacoes || '');
    const [guardando, setGuardando] = useState(false);
    const [err, setErr] = useState('');

    const temNC = Object.values(checklist).some(c => c.status === 'NC');
    const [apto, setApto] = useState(verificacao ? verificacao.resultado_apto : true);

    // Enquanto o utilizador não decidir ao contrário, o resultado segue o
    // checklist: uma NC torna o RMM inapto.
    useEffect(() => { setApto(!temNC); }, [temNC]);

    const setItem = (key, campo, valor) =>
        setChecklist(c => ({ ...c, [key]: { ...c[key], [campo]: valor } }));

    const guardar = async (comPdf) => {
        setGuardando(true);
        setErr('');
        try {
            const r = await api.post(`/sistema-integrado/rmm/${rmm.id}/verificacoes`, {
                ano, mes,
                data_verificacao: data,
                checklist,
                resultado_apto: apto,
                observacoes,
                verificado_por: verificadoPor || null,
                nome_verificador: trabalhadores.find(t => String(t.id) === String(verificadoPor))?.nome || null,
            });
            if (comPdf) {
                const pdf = await api.get(`/sistema-integrado/rmm/verificacoes/${r.data.id}/relatorio`, { responseType: 'blob' });
                descarregarBlob(pdf.data, nomeRelatorio(r.data));
            }
            onSaved();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar a verificação.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 900 }} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 4px' }}>
                    Verificação interna — {rmm.codigo} · {MESES[mes - 1]} {ano}
                </h3>
                <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.85rem' }}>
                    {rmm.designacao || rmm.marca || '—'} · critérios do IT.IM.01
                </p>

                <div data-liquid-grid="true" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Data da verificação</label>
                        <input type="date" value={data} onChange={e => setData(e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Verificado por</label>
                        <select value={verificadoPor} onChange={e => setVerificadoPor(e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">— selecionar —</option>
                            {trabalhadores.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                        </select>
                    </div>
                </div>

                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '8px 10px', fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                        <div style={{ flex: 1 }}>ITEM DE VERIFICAÇÃO</div>
                        <div style={{ width: 130, textAlign: 'center' }}>NA · C · NC</div>
                        <div style={{ width: 180 }}>OBSERVAÇÕES</div>
                    </div>

                    {itens.map((item, idx) => item.grupo ? (
                        <div key={`g${idx}`} style={{ background: '#f1f5f9', padding: '6px 10px', fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>
                            {item.grupo}
                        </div>
                    ) : (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderTop: '1px solid #f1f5f9' }}>
                            <div style={{ flex: 1, fontSize: '0.82rem' }}><LiquidText>{item.texto}</LiquidText></div>
                            <div style={{ width: 130, display: 'flex', gap: 4, justifyContent: 'center' }}>
                                {['NA', 'C', 'NC'].map(s => {
                                    const ativo = checklist[`item${item.id}`]?.status === s;
                                    const cor = s === 'C' ? '#16a34a' : s === 'NC' ? '#dc2626' : '#64748b';
                                    return (
                                        <button key={s} onClick={() => setItem(`item${item.id}`, 'status', s)}
                                            style={{
                                                width: 38, padding: '4px 0', borderRadius: 6, cursor: 'pointer',
                                                fontSize: '0.72rem', fontWeight: 700,
                                                border: `1px solid ${ativo ? cor : '#cbd5e1'}`,
                                                background: ativo ? cor : '#fff',
                                                color: ativo ? '#fff' : '#64748b',
                                            }}>{s}</button>
                                    );
                                })}
                            </div>
                            <input value={checklist[`item${item.id}`]?.obs || ''}
                                onChange={e => setItem(`item${item.id}`, 'obs', e.target.value)}
                                style={{ ...inp, width: 180, fontSize: '0.8rem', padding: '5px 8px' }} />
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: 14 }}>
                    <label data-liquid-label="true" style={lbl}>Observações gerais</label>
                    <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
                        rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                <div style={{
                    marginTop: 14, padding: '10px 14px', borderRadius: 8,
                    background: apto ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${apto ? '#bbf7d0' : '#fecaca'}`,
                }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                        <input type="checkbox" checked={apto} onChange={e => setApto(e.target.checked)} />
                        <span style={{ fontWeight: 700, color: apto ? '#166534' : '#991b1b' }}>
                            {apto ? 'RMM apto para utilização' : 'RMM inapto — segregar e etiquetar como fora de serviço'}
                        </span>
                    </label>
                    {!apto && (
                        <p style={{ margin: '6px 0 0 28px', fontSize: '0.78rem', color: '#991b1b' }}>
                            Ao guardar, o estado do RMM passa a «Fora de serviço». Fitas métricas e réguas de nível
                            são substituídas por novas (IT.IM.01), não reparadas.
                        </p>
                    )}
                </div>

                <p style={{ marginTop: 10, fontSize: '0.75rem', color: '#94a3b8' }}>
                    IT.IM.01: os resultados obtidos pela medição não são fator decisor para a libertação
                    do produto ou serviço, mas auxiliam o processo de decisão.
                </p>

                {err && <div style={{ color: '#e53e3e', marginTop: 10, fontSize: '0.85rem' }}>{err}</div>}

                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-secondary="true" onClick={() => guardar(false)} disabled={guardando} style={btnSec}>
                        {guardando ? 'A guardar…' : 'Guardar'}
                    </button>
                    <button data-liquid-primary="true" onClick={() => guardar(true)} disabled={guardando} style={btnPri}>
                        <LiquidText>{guardando ? 'A guardar…' : '📄 Guardar e gerar PDF'}</LiquidText>
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Relatórios das verificações ----------
   Doze meses vezes os instrumentos dá centenas de PDF por ano: em vez de uma
   tira de botões que não acaba, uma grelha por ano — instrumentos em linha,
   meses em coluna, cada célula o relatório desse mês. Os anos carregam-se à
   medida que se abrem e ficam em memória enquanto o modal estiver aberto. */
function RelatoriosModal({ anos, anoInicial, rmms, onAbrir, onClose }) {
    const [ano, setAno] = useState(anoInicial);
    const [porAno, setPorAno] = useState({});
    const [aCarregar, setACarregar] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (porAno[ano]) return undefined;
        let cancelado = false;
        setACarregar(true);
        setErr('');
        api.get('/sistema-integrado/rmm/verificacoes', { params: { ano } })
            .then(r => { if (!cancelado) setPorAno(x => ({ ...x, [ano]: r.data || [] })); })
            .catch(() => { if (!cancelado) setErr('Não foi possível carregar os relatórios deste ano.'); })
            .finally(() => { if (!cancelado) setACarregar(false); });
        return () => { cancelado = true; };
    }, [ano, porAno]);

    const doAno = porAno[ano] || [];
    const comRelatorio = rmms.filter(r => doAno.some(v => v.rmm_id === r.id));
    const naoConformes = doAno.filter(v => !v.resultado_apto).length;

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 1000 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                        <h3 style={{ margin: '0 0 4px' }}>Relatórios das verificações</h3>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>
                            Cada célula é o relatório desse mês, em PDF (IT.IM.01).
                        </p>
                    </div>
                    <button data-liquid-secondary="true" onClick={onClose} style={{ ...btnSec, padding: '4px 10px' }}>Fechar</button>
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '14px 0 12px' }}>
                    {anos.map(a => (
                        <button data-liquid-secondary="true" key={a} onClick={() => setAno(a)}
                            style={{
                                ...btnSec, padding: '5px 12px', fontWeight: a === ano ? 700 : 500,
                                ...(a === ano ? { borderColor: '#3182ce', background: '#ebf8ff', color: '#1e40af' } : {}),
                            }}>{a}</button>
                    ))}
                    <span data-liquid-tone={liquidTone('#f1f5f9')} data-liquid-badge="true" style={{ ...badge, background: '#f1f5f9', color: '#475569', alignSelf: 'center', marginLeft: 4 }}>
                        {doAno.length} relatório(s){naoConformes ? ` · ${naoConformes} não conforme(s)` : ''}
                    </span>
                </div>

                {err && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: 10 }}>{err}</div>}

                {aCarregar ? <p style={{ color: '#718096' }}>A carregar…</p>
                    : !comRelatorio.length ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
                            Não há verificações registadas em {ano}.
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="standard-table" style={{ minWidth: 720 }}>
                                <thead>
                                    <tr>
                                        <th>Instrumento</th>
                                        {MESES_CURTO.map((m, i) => (
                                            <th key={i} title={MESES[i]} style={{ textAlign: 'center', width: 44, padding: '8px 2px' }}>{m}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {comRelatorio.map(r => (
                                        <tr key={r.id}>
                                            <td>
                                                <div style={{ fontWeight: 700 }}>{r.codigo}</div>
                                                <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{r.designacao || '—'}</div>
                                            </td>
                                            {MESES_CURTO.map((_, i) => {
                                                const v = doAno.find(x => x.rmm_id === r.id && x.mes === i + 1);
                                                if (!v) return <td key={i} style={{ textAlign: 'center', color: '#e2e8f0' }}>·</td>;
                                                const cor = v.resultado_apto ? '#16a34a' : '#dc2626';
                                                return (
                                                    <td key={i} style={{ textAlign: 'center', padding: '4px 2px' }}>
                                                        <button onClick={() => onAbrir(v)}
                                                            title={[
                                                                `${MESES[i]} de ${ano}`,
                                                                v.relatorio_num,
                                                                v.data_verificacao && new Date(v.data_verificacao).toLocaleDateString('pt-PT'),
                                                                v.resultado_apto ? 'Apto' : 'Inapto',
                                                                v.nome_verificador || v.verificador_nome,
                                                            ].filter(Boolean).join(' · ')}
                                                            style={{
                                                                width: 34, height: 26, borderRadius: 6, cursor: 'pointer',
                                                                border: `1px solid ${cor}`, background: '#fff', color: cor,
                                                                fontSize: '0.72rem', fontWeight: 700, lineHeight: 1,
                                                            }}>
                                                            <LiquidText>{v.resultado_apto ? '✓' : '✗'}</LiquidText>
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Meses em falta, de uma vez ----------
   Quem faz a verificação mensal de uma fita métrica ou de um laser olha para
   ela e segue; o registo é que fica para trás. Isto fecha os meses que faltam
   no ano, todos Conformes e cada um com a data do fim do respetivo mês. Um mês
   com problema volta-se a abrir pela grelha, que substitui o registo. */
function EmFaltaModal({ rmm, ano, meses, trabalhadores, onClose, onFeito }) {
    const [verificadoPor, setVerificadoPor] = useState(rmm.trabalhador_id || '');
    const [observacoes, setObservacoes] = useState('');
    const [aGravar, setAGravar] = useState(false);
    const [err, setErr] = useState('');

    const nomes = meses.map(m => MESES[m - 1]);
    const intervalo = nomes.length > 2 ? `${nomes[0]} a ${nomes[nomes.length - 1]}` : nomes.join(' e ');

    const gravar = async () => {
        setAGravar(true);
        setErr('');
        try {
            await api.post(`/sistema-integrado/rmm/${rmm.id}/verificacoes/em-falta`, {
                ano,
                verificado_por: verificadoPor || null,
                nome_verificador: trabalhadores.find(t => String(t.id) === String(verificadoPor))?.nome || null,
                observacoes,
            });
            onFeito();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao registar as verificações.');
        } finally {
            setAGravar(false);
        }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 4px' }}>Verificar os meses em falta — {rmm.codigo}</h3>
                <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: '0.85rem' }}>
                    {rmm.designacao || rmm.marca || '—'}
                </p>

                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: '0.86rem', color: '#334155' }}>
                    Vão ficar registadas <b>{meses.length} verificações</b> de {ano} ({intervalo}),
                    com todos os pontos do IT.IM.01 como <b>Conforme</b> e o RMM apto.
                    Cada uma fica com a data do último dia do seu mês — a do mês corrente com a data de hoje.
                </div>

                <div style={{ marginTop: 14 }}>
                    <label data-liquid-label="true" style={lbl}>Verificado por</label>
                    <select value={verificadoPor} onChange={e => setVerificadoPor(e.target.value)} style={{ ...inp, width: '100%' }}>
                        <option value="">— selecionar —</option>
                        {trabalhadores.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                    </select>
                </div>

                <div style={{ marginTop: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Observações (ficam nas {meses.length} verificações)</label>
                    <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2}
                        placeholder="Ex.: regularização do registo das verificações mensais do ano."
                        style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                {err && <div style={{ color: '#e53e3e', marginTop: 10, fontSize: '0.85rem' }}>{err}</div>}

                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={gravar} disabled={aGravar} style={btnPri}>
                        {aGravar ? 'A registar…' : `Registar ${meses.length} verificações`}
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Cruzamento com as fichas de Equipamentos ----------
   As fichas da categoria RMM que ainda não têm registo no Sistema Integrado.
   Cria-se um RMM por cada uma, com o tipo e o código já propostos pelo
   servidor (o tipo manda na check-list do IT.IM.01, por isso confirma-se). */
function Cruzamento({ dados, tipos, onImportado }) {
    const porCruzar = (dados?.equipamentos || []).filter(e => !e.rmm_id);
    const [escolhas, setEscolhas] = useState({});
    const [aImportar, setAImportar] = useState(false);
    const [err, setErr] = useState('');

    // Sempre que a lista muda (importou, chegou ficha nova), repõe as propostas
    // do servidor com tudo marcado: o caso normal é importar tudo de uma vez.
    useEffect(() => {
        setEscolhas(porCruzar.reduce((acc, e) => {
            acc[e.id] = {
                sel: true,
                tipo: e.tipo_sugerido || 'fita_metrica',
                codigo: e.codigo_sugerido || '',
                existente: !!e.codigo_existente,
            };
            return acc;
        }, {}));
        // Reiniciar apenas quando mudam os IDs, preservando as escolhas durante a edicao.
    }, [porCruzar.map(e => e.id).join(',')]);

    if (!dados) return null;
    const semFicha = dados.rmmSemFicha || 0;
    if (!porCruzar.length && !semFicha) return null;

    const set = (id, campo, valor) => setEscolhas(x => ({ ...x, [id]: { ...x[id], [campo]: valor } }));
    const marcados = porCruzar.filter(e => escolhas[e.id]?.sel);

    const importar = async () => {
        if (!marcados.length) return;
        setAImportar(true);
        setErr('');
        try {
            const r = await api.post('/sistema-integrado/rmm/importar', {
                itens: marcados.map(e => ({
                    equipamento_id: e.id,
                    tipo: escolhas[e.id].tipo,
                    codigo: (escolhas[e.id].codigo || '').trim(),
                })),
            });
            const ignorados = r.data?.ignorados || [];
            if (ignorados.length) setErr(ignorados.map(i => `${i.nome || i.equipamento_id}: ${i.motivo}`).join(' · '));
            onImportado();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao criar os RMM.');
        } finally {
            setAImportar(false);
        }
    };

    return (
        <div data-liquid-notice="true" data-liquid-tone={liquidTone('#f0f9ff')} style={{ border: '1px solid #bae6fd', background: '#f0f9ff', borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 4px', fontSize: '0.92rem', color: '#075985' }}><LiquidText>
                🔗 Cruzamento com Equipamentos &amp; Ferramentas
            </LiquidText></h4>
            <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: '#0c4a6e' }}>
                O inventário que conta é o das fichas de equipamento com categoria RMM.
                Cada ficha passa a ter aqui um código permanente e as 12 verificações do ano.
                Um código que já exista sem instrumento recebe esta ficha — não se cria outro.
            </p>

            {porCruzar.length > 0 && (
                <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {porCruzar.map(e => {
                            const esc = escolhas[e.id] || {};
                            return (
                                <div key={e.id} style={{
                                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                                    background: '#fff', border: '1px solid #e0f2fe', borderRadius: 8, padding: '7px 10px',
                                }}>
                                    <input type="checkbox" checked={!!esc.sel} onChange={ev => set(e.id, 'sel', ev.target.checked)} />
                                    <div style={{ flex: '1 1 260px', minWidth: 200 }}>
                                        <div style={{ fontWeight: 600, fontSize: '0.86rem' }}>{e.nome}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                            {[e.n_serie && `nº ${e.n_serie}`, e.trabalhador_nome || e.registado_por_nome].filter(Boolean).join(' · ') || '—'}
                                        </div>
                                    </div>
                                    <select value={esc.tipo || 'fita_metrica'} onChange={ev => set(e.id, 'tipo', ev.target.value)}
                                        style={{ ...inp, padding: '5px 8px', fontSize: '0.82rem' }}>
                                        {tipos.map(t => <option key={t.valor} value={t.valor}>{t.label}</option>)}
                                    </select>
                                    <div>
                                        <input value={esc.codigo || ''} onChange={ev => set(e.id, 'codigo', ev.target.value)}
                                            title="Código permanente do RMM. Se já existir um código sem instrumento, é esse que o recebe."
                                            style={{ ...inp, width: 90, padding: '5px 8px', fontSize: '0.82rem' }} />
                                        <div style={{ fontSize: '0.68rem', color: esc.existente ? '#075985' : '#64748b', marginTop: 2 }}>
                                            {esc.existente ? 'código existente' : 'código novo'}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                        <button data-liquid-primary="true" onClick={importar} disabled={aImportar || !marcados.length} style={btnPri}>
                            {aImportar ? 'A cruzar…' : `Cruzar ${marcados.length} ficha(s)`}
                        </button>
                        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                            {porCruzar.length} ficha(s) de equipamento por cruzar
                        </span>
                    </div>
                </>
            )}

            {semFicha > 0 && (
                <p style={{ margin: porCruzar.length ? '12px 0 0' : 0, fontSize: '0.8rem', color: '#92400e' }}><LiquidText>
                    ⚠️ </LiquidText>{semFicha} RMM {semFicha === 1 ? 'não tem' : 'não têm'}<LiquidText> ficha de equipamento.
                    Abra o RMM em ✎ e ligue-o à ficha correspondente, ou elimine-o se já não existe.
                </LiquidText></p>
            )}

            {err && <div style={{ color: '#b91c1c', marginTop: 8, fontSize: '0.8rem' }}>{err}</div>}
        </div>
    );
}

/* ---------- Componente principal ---------- */
const Rmm = () => {
    const anoAtual = new Date().getFullYear();
    const [ano, setAno] = useState(anoAtual);
    const [anosExistentes, setAnosExistentes] = useState([]);
    const [rmms, setRmms] = useState([]);
    const [verificacoes, setVerificacoes] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [checklists, setChecklists] = useState({});
    const [trabalhadores, setTrabalhadores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editarRmm, setEditarRmm] = useState(null);
    const [verificar, setVerificar] = useState(null);
    const [cruzamento, setCruzamento] = useState(null);
    // ?rmm=<id> — a ficha de onde se veio, para a destacar na grelha
    const [destaque] = useState(() => new URLSearchParams(window.location.search).get('rmm') || null);
    const [regularizar, setRegularizar] = useState(null);
    const [relatorios, setRelatorios] = useState(false);

    // O catálogo de tipos/checklists e os trabalhadores não dependem do ano.
    useEffect(() => {
        api.get('/sistema-integrado/rmm/checklists')
            .then(r => { setTipos(r.data.tipos || []); setChecklists(r.data.checklists || {}); })
            .catch(() => { });
        api.get('/trabalhadores')
            .then(r => setTrabalhadores((r.data || []).filter(t => t.ativo !== false)))
            .catch(() => { });
    }, []);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [rl, rv, ra, rc] = await Promise.all([
                api.get('/sistema-integrado/rmm'),
                api.get('/sistema-integrado/rmm/verificacoes', { params: { ano } }),
                api.get('/sistema-integrado/rmm/anos'),
                api.get('/sistema-integrado/rmm/equipamentos'),
            ]);
            setRmms(rl.data || []);
            setVerificacoes(rv.data || []);
            setAnosExistentes(Array.isArray(ra.data) ? ra.data : []);
            setCruzamento(rc.data || null);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [ano]);
    useEffect(() => { carregar(); }, [carregar]);

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 4; a--) anos.push(a);
    anosExistentes.forEach(a => { if (!anos.includes(a)) anos.push(a); });
    anos.sort((x, y) => y - x);

    const verificacaoDe = (rmmId, mes) =>
        verificacoes.find(v => v.rmm_id === rmmId && v.mes === mes);

    const eliminarRmm = async (r) => {
        if (!window.confirm(`Eliminar o RMM ${r.codigo} e todas as suas verificações?`)) return;
        try { await api.delete(`/sistema-integrado/rmm/${r.id}`); carregar(); }
        catch { alert('Erro ao eliminar.'); }
    };

    const abrirPdf = async (v) => {
        try {
            const pdf = await api.get(`/sistema-integrado/rmm/verificacoes/${v.id}/relatorio`, { responseType: 'blob' });
            abrirFicheiroDaResposta(pdf, nomeRelatorio(v));
        } catch { alert('Erro ao gerar o PDF.'); }
    };

    // Meses já decorridos no ano — só esses contam como "em falta".
    const mesesDecorridos = ano < anoAtual ? 12 : ano > anoAtual ? 0 : new Date().getMonth() + 1;
    const ativos = rmms.filter(r => r.estado === 'ativo');
    const contaveis = ativos.filter(r => r.equipamento_id);
    const emFalta = contaveis.reduce((acc, r) => {
        for (let m = 1; m <= mesesDecorridos; m++) if (!verificacaoDe(r.id, m)) acc++;
        return acc;
    }, 0);
    const naoConformes = verificacoes.filter(v => !v.resultado_apto).length;

    // Meses já decorridos ainda sem verificação, do instrumento indicado.
    const mesesEmFalta = (r) => {
        const lista = [];
        for (let m = 1; m <= mesesDecorridos; m++) if (!verificacaoDe(r.id, m)) lista.push(m);
        return lista;
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ano das verificações</label>
                        <select value={ano} onChange={e => setAno(parseInt(e.target.value, 10))} style={inp}>
                            {anos.map(a => <option key={a} value={a}>{a}{anosExistentes.includes(a) ? '' : ' (vazio)'}</option>)}
                        </select>
                    </div>
                    {rmms.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, paddingBottom: 4, flexWrap: 'wrap' }}>
                            <span data-liquid-tone={liquidTone('#e0f2fe')} data-liquid-badge="true" style={{ ...badge, background: '#e0f2fe', color: '#075985' }}
                                title="Instrumentos com ficha de equipamento / total de códigos">
                                {rmms.length} RMM · {contaveis.length} com ficha
                            </span>
                            <span data-liquid-tone={liquidTone(emFalta ? '#fef3c7' : '#dcfce7')} data-liquid-badge="true" style={{ ...badge, background: emFalta ? '#fef3c7' : '#dcfce7', color: emFalta ? '#92400e' : '#166534' }}
                                title="Só contam os RMM cruzados com uma ficha de equipamento — um código sem instrumento não tem verificações por fazer">
                                {emFalta ? `${emFalta} verificações em falta` : 'Sem verificações em falta'}
                            </span>
                            {naoConformes > 0 && (
                                <span data-liquid-tone={liquidTone('#fee2e2')} data-liquid-badge="true" style={{ ...badge, background: '#fee2e2', color: '#991b1b' }}>
                                    {naoConformes} não conformes
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div data-liquid-actions="true" style={{ display: 'flex', gap: 8 }}>
                    <button data-liquid-secondary="true" onClick={() => setRelatorios(true)} style={btnSec}
                        title="Todos os relatórios das verificações, arrumados por ano"><LiquidText>
                        📄 Relatórios
                    </LiquidText></button>
                    <button data-liquid-primary="true" onClick={() => setEditarRmm({})} style={btnPri}>+ Novo RMM</button>
                </div>
            </div>

            <Cruzamento dados={cruzamento} tipos={tipos} onImportado={carregar} />

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : rmms.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                        Ainda não há RMM registados. Os instrumentos entram pela ficha de
                        equipamento (categoria RMM) e recebem aqui um código permanente
                        (FM/RN/LS) e as verificações mensais.
                    </div>
                ) : (
                    <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                        <table className="standard-table" style={{ minWidth: 900 }}>
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Tipo</th>
                                    <th>Designação</th>
                                    <th>Afeto a</th>
                                    <th style={{ textAlign: 'center' }}>Ficha</th>
                                    <th style={{ textAlign: 'center' }}>Estado</th>
                                    {MESES_CURTO.map((m, i) => (
                                        <th key={i} title={MESES[i]} style={{ textAlign: 'center', width: 30, padding: '8px 2px' }}>{m}</th>
                                    ))}
                                    <th style={{ textAlign: 'center' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rmms.map(r => {
                                    const est = ESTADOS[r.estado] || ESTADOS.ativo;
                                    const tipoLabel = tipos.find(t => t.valor === r.tipo)?.label || r.tipo;
                                    const realcado = destaque && String(r.id) === String(destaque);
                                    return (
                                        <tr key={r.id}
                                            ref={realcado ? (el => el?.scrollIntoView({ block: 'center' })) : undefined}
                                            style={realcado ? { background: '#fefce8', outline: '2px solid #facc15' } : undefined}>
                                            <td style={{ fontWeight: 700 }}>{r.codigo}</td>
                                            <td>{tipoLabel}</td>
                                            <td>{r.designacao || '—'}</td>
                                            <td>{r.trabalhador_nome || <span style={{ color: '#cbd5e1' }}>— sem afetação —</span>}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                {r.equipamento_id
                                                    ? <a href="/equipamentos" title={`Ficha de equipamento: ${r.equipamento_nome} — abrir para editar ou imprimir a etiqueta QR`}
                                                        data-liquid-badge="true" data-liquid-tone="info" style={{ ...badge, background: '#e0f2fe', color: '#075985', textDecoration: 'none', display: 'inline-block' }}><LiquidText>🔗 Ligado</LiquidText></a>
                                                    : <button onClick={() => setEditarRmm(r)} title="Ligar à ficha de equipamento correspondente"
                                                        style={{ ...badge, background: '#fef3c7', color: '#92400e', border: 'none', cursor: 'pointer' }}>Sem ficha</button>}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span data-liquid-tone={liquidTone(est.bg)} data-liquid-badge="true" style={{ ...badge, background: est.bg, color: est.fg }}><LiquidText>{est.label}</LiquidText></span>
                                            </td>
                                            {MESES_CURTO.map((_, i) => {
                                                const mes = i + 1;
                                                const v = verificacaoDe(r.id, mes);
                                                const futuro = ano > anoAtual || (ano === anoAtual && mes > new Date().getMonth() + 1);
                                                const cor = v ? (v.resultado_apto ? '#16a34a' : '#dc2626') : futuro ? '#e2e8f0' : '#f59e0b';
                                                return (
                                                    <td key={i} style={{ textAlign: 'center', padding: '4px 2px' }}>
                                                        <button
                                                            onClick={() => setVerificar({ rmm: r, mes })}
                                                            title={v
                                                                ? `${MESES[i]} — ${v.resultado_apto ? 'Apto' : 'Inapto'} (${v.relatorio_num || ''})`
                                                                : `${MESES[i]} — registar verificação`}
                                                            style={{
                                                                width: 24, height: 24, borderRadius: 6, cursor: 'pointer',
                                                                border: `1px solid ${cor}`, background: v ? cor : '#fff',
                                                                color: v ? '#fff' : cor, fontSize: '0.7rem', fontWeight: 700, lineHeight: 1,
                                                            }}>
                                                            <LiquidText>{v ? (v.resultado_apto ? '✓' : '✗') : futuro ? '' : '!'}</LiquidText>
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                    const faltam = mesesEmFalta(r);
                                                    return (
                                                        <button data-liquid-secondary="true" onClick={() => setRegularizar({ rmm: r, meses: faltam })}
                                                            disabled={!faltam.length}
                                                            title={faltam.length
                                                                ? `Verificar os ${faltam.length} meses em falta até hoje (${faltam.map(m => MESES[m - 1]).join(', ')})`
                                                                : 'Não há meses em falta neste ano'}
                                                            style={{
                                                                ...btnSec, ...mini, marginLeft: 0,
                                                                ...(faltam.length
                                                                    ? { borderColor: '#86efac', background: '#f0fdf4', color: '#166534' }
                                                                    : { opacity: 0.35, cursor: 'not-allowed' }),
                                                            }}><LiquidText>✓✓</LiquidText></button>
                                                    );
                                                })()}
                                                <button data-liquid-secondary="true" onClick={() => setEditarRmm(r)} style={{ ...btnSec, ...mini }}><LiquidText>✎</LiquidText></button>
                                                <button data-liquid-secondary="true" onClick={() => eliminarRmm(r)} style={{ ...btnSec, ...mini, color: '#dc2626' }}><DeleteIcon /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

            {relatorios && (
                <RelatoriosModal
                    anos={[...new Set([...anosExistentes, ano])].sort((a, b) => b - a)}
                    anoInicial={ano}
                    rmms={rmms}
                    onAbrir={abrirPdf}
                    onClose={() => setRelatorios(false)} />
            )}

            {editarRmm && (
                <RmmModal rmm={editarRmm} tipos={tipos} trabalhadores={trabalhadores}
                    equipamentos={cruzamento?.equipamentos || []}
                    onClose={() => setEditarRmm(null)} onSaved={carregar} />
            )}
            {regularizar && (
                <EmFaltaModal
                    rmm={regularizar.rmm}
                    ano={ano}
                    meses={regularizar.meses}
                    trabalhadores={trabalhadores}
                    onClose={() => setRegularizar(null)}
                    onFeito={carregar} />
            )}
            {verificar && (
                <VerificacaoModal
                    rmm={verificar.rmm}
                    ano={ano}
                    mes={verificar.mes}
                    verificacao={verificacaoDe(verificar.rmm.id, verificar.mes)}
                    itens={checklists[verificar.rmm.tipo] || []}
                    trabalhadores={trabalhadores}
                    onClose={() => setVerificar(null)}
                    onSaved={carregar} />
            )}
        </div>
    );
};

const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const lido = { padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', background: '#f8fafc', color: '#475569', minHeight: 20 };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '5px 10px', marginLeft: 6, fontSize: '0.78rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };

export default Rmm;
